import { ref, computed, isRef } from 'vue';
import { useStepperZonas } from './useStepperZonas';
import { useStepperButacas } from './useStepperButacas';
import { useStepperDatos } from './useStepperDatos';
import { useStepperResumen } from './useStepperResumen';
import { useStepperPago } from './useStepperPago';
import { detectarTipoDocumento, toDateInputFormat } from './utils';

// ─── Constantes del stepper ────────────────────────────────────────────────
export const PASO_ZONA    = 1;
export const PASO_BUTACAS = 2;
export const PASO_DATOS   = 3;
export const PASO_RESUMEN = 4;
export const PASO_PAGO    = 5;
export const TOTAL_PASOS  = 5;

/**
 * useGenerarInvitaciones — Fuente de verdad del stepper de generación de invitaciones.
 *
 * Orquesta el flujo multi-paso componiendo sub-composables especializados.
 * La navegación y los guards cross-domain son responsabilidad exclusiva de este orquestador.
 *
 * Contrato de uso: idéntico al original. Ver JSDoc en useGenerarInvitaciones.js (nivel plano).
 *
 * @param {import('vue').Ref<string|number>|string|number} abonadoIdSource  ID del abonado
 */
export function useGenerarInvitaciones(abonadoIdSource) {

    // ─── Helper ───────────────────────────────────────────────────────────
    const getAbonadoId = () => {
        if (isRef(abonadoIdSource)) return abonadoIdSource.value;
        if (typeof abonadoIdSource === 'function') return abonadoIdSource();
        return abonadoIdSource;
    };

    // ─── Estado compartido del stepper ────────────────────────────────────
    const pasoActual = ref(PASO_ZONA);
    const evento = ref(null);
    const _statsRestantes = ref(null);

    // ─── Computed: límite de asientos (antes de butacas, que lo recibe) ───
    const maxAsientos = computed(() => {
        if (!evento.value) return 0;
        const eventoLimit = evento.value.invitaciones_restantes ?? 0;
        if (_statsRestantes.value !== null) {
            return Math.min(eventoLimit, _statsRestantes.value);
        }
        return eventoLimit;
    });

    // ─── Composición de sub-composables ───────────────────────────────────
    const zonas   = useStepperZonas(evento, getAbonadoId);
    const butacas = useStepperButacas(evento, getAbonadoId, zonas.zonaSeleccionada, maxAsientos);
    const datos   = useStepperDatos(evento, getAbonadoId, butacas.asientosSeleccionados, zonas.zonaSeleccionada, butacas.sessionId);
    const resumen = useStepperResumen(evento, getAbonadoId, butacas.sessionId);
    const pago    = useStepperPago(butacas.sessionId);

    // ─── Computed: guards cross-domain ────────────────────────────────────

    /**
     * `true` cuando el composable está listo para comenzar el stepper.
     */
    const puedeIniciarFlujo = computed(
        () => Boolean(evento.value) && (evento.value.invitaciones_restantes ?? 0) > 0
    );

    /**
     * Paso 4 → puede finalizar o avanzar a pago si los términos están aceptados.
     */
    const puedeFinalizarPasoResumen = computed(
        () => resumen.terminosAceptados.value?.privacidad
            && resumen.terminosAceptados.value?.condiciones
            && !datos.pagoRequerido.value
    );

    /**
     * Flag de expiración de timer. La vista padre lo observa para volver al dashboard.
     */
    const timerExpirado = ref(false);

    // ─── Métodos wrapped: Paso 1 (cross-domain) ───────────────────────────

    /**
     * Selecciona una zona, resetea butacas y avanza automáticamente al Paso 2.
     * Si la zona ya estaba seleccionada, la deselecciona (sin avanzar).
     */
    const seleccionarZona = zona => {
        const isNewSelection = zonas.seleccionarZona(zona);
        if (isNewSelection) {
            butacas.reset();
            // Avanzar al Paso 2
            if (pasoActual.value === PASO_ZONA && zonas.puedeAvanzarPasoZona.value) {
                pasoActual.value = PASO_BUTACAS;
                butacas.fetchAsientos();
            }
        }
    };

    /**
     * Limpia la zona seleccionada y resetea el estado asociado de butacas.
     */
    const limpiarZona = () => {
        zonas.limpiarZona();
        butacas.reset();
    };

    // ─── Navegación ───────────────────────────────────────────────────────

    /**
     * Avanza al siguiente paso con los efectos secundarios necesarios.
     */
    const nextStep = async() => {
        if (pasoActual.value === PASO_ZONA) {
            if (!zonas.puedeAvanzarPasoZona.value) return;
            pasoActual.value = PASO_BUTACAS;
            await butacas.fetchAsientos();
            return;
        }

        if (pasoActual.value === PASO_BUTACAS) {
            if (!butacas.puedeAvanzarPasoButacas.value) return;
            try {
                await butacas.bloquearAsientos();
                datos.inicializarCamposCustom();
                datos.inicializarDatosPorButaca();
                pasoActual.value = PASO_DATOS;
            } catch (_) {
                // Error ya manejado dentro de bloquearAsientos
            }
            return;
        }

        if (pasoActual.value === PASO_DATOS) {
            if (!datos.puedeAvanzarPasoDatos.value) return;
            const canAdvance = await datos.guardarDatos();
            if (canAdvance) {
                pasoActual.value = PASO_RESUMEN;
            }
            return;
        }

        if (pasoActual.value === PASO_RESUMEN && datos.pagoRequerido.value) {
            pasoActual.value = PASO_PAGO;
            return;
        }

        // Paso 5 (pago): la finalización se maneja externamente
    };

    /**
     * Retrocede al paso anterior con los efectos secundarios necesarios.
     */
    const prevStep = async() => {
        if (pasoActual.value === PASO_DATOS) {
            await butacas.liberarSesion();
            datos.resetParcial();
            butacas.limpiarAsientosSeleccionados();
            pasoActual.value = PASO_BUTACAS;
            await butacas.fetchAsientos();
            return;
        }

        if (pasoActual.value === PASO_BUTACAS) {
            butacas.limpiarAsientosSeleccionados();
            // Limpiar zona: en el original se usaba limpiarZona() que también
            // resetea butacas, pero como ya estamos saliendo de butacas,
            // solo limpiamos la zona
            zonas.limpiarZona();
            pasoActual.value = PASO_ZONA;
            return;
        }

        if (pasoActual.value === PASO_RESUMEN) {
            pasoActual.value = PASO_DATOS;
            return;
        }

        if (pasoActual.value === PASO_PAGO) {
            pasoActual.value = PASO_RESUMEN;
            return;
        }
    };

    /**
     * Cancela el flujo completo. Libera la sesión si existe y resetea todo.
     */
    const cancelar = async() => {
        await butacas.liberarSesion();
        _resetAll();
    };

    // ─── Guardar datos y avanzar (backward compat con API original) ───────

    /**
     * Guarda los datos del comprador y, si no requiere pago, avanza al Paso 4.
     * Mantiene la API original para compatibilidad con componentes existentes.
     */
    const guardarDatosYAvanzar = async() => {
        const canAdvance = await datos.guardarDatos();
        if (canAdvance) {
            pasoActual.value = PASO_RESUMEN;
        }
    };

    // ─── Procesamiento final (Paso 4) ─────────────────────────────────────

    /**
     * Ejecuta la compra sin gastos de gestión.
     */
    const procesarCompra = async() => {
        if (!puedeFinalizarPasoResumen.value) return;
        const success = await resumen.procesarCompra();
        if (success) {
            butacas.limpiarSessionId();
        }
    };

    // ─── Timer externo ────────────────────────────────────────────────────

    /**
     * Callback que debe llamar el componente CuentaAtras cuando el timer llega a 0.
     */
    const onTimerExpirado = async() => {
        timerExpirado.value = true;
        await butacas.liberarSesion();
        _resetAll();
        timerExpirado.value = false;
    };

    // ─── Inicialización ───────────────────────────────────────────────────

    /**
     * Punto de entrada del stepper.
     */
    const iniciar = (eventoObj, statsObj = null, datosAbonado = null) => {
        _resetAll();
        evento.value = eventoObj;
        if (statsObj !== null && typeof statsObj.restantes === 'number') {
            _statsRestantes.value = statsObj.restantes;
        } else {
            _statsRestantes.value = null;
        }

        // Precargar formulario con datos del abonado autenticado
        if (datosAbonado) {
            const dni = datosAbonado.dni ?? datosAbonado.document ?? '';
            const email = datosAbonado.email ?? '';
            datos.formDatosComprador.value = {
                nombre:           datosAbonado.name       ?? datosAbonado.nombre ?? '',
                apellidos:        datosAbonado.surnames   ?? datosAbonado.apellidos ?? '',
                tipoDocumento:    datosAbonado.tipoDocumentoSeleccionado
                                  ?? detectarTipoDocumento(dni),
                dni:              dni,
                email:            email,
                emailConfirmacion: email,
                fecha_nacimiento: toDateInputFormat(
                    datosAbonado.fecha_nacimiento?.trim()
                    || datosAbonado.fechaDeNacimiento?.trim()
                    || datosAbonado.birth_date?.trim()
                    || datosAbonado.date_of_birth?.trim()
                    || datosAbonado.nacimiento?.trim()
                    || datosAbonado.fechaNacimiento?.trim()
                    || ''
                ),
                telefono:         datosAbonado.telefono   ?? datosAbonado.phone ?? '',
                cp:               datosAbonado.cp         ?? datosAbonado.postal_code ?? '',
            };
        }

        zonas.fetchZonas();
        zonas.fetchSvgRecinto();
    };

    /**
     * Inyecta externamente el valor de restantes del abonado para maxAsientos.
     */
    const setStatsRestantes = restantes => {
        _statsRestantes.value = typeof restantes === 'number' ? restantes : null;
    };

    // ─── Reset completo ───────────────────────────────────────────────────
    const _resetAll = () => {
        pasoActual.value = PASO_ZONA;
        evento.value = null;
        _statsRestantes.value = null;
        zonas.reset();
        butacas.reset();
        datos.reset();
        resumen.reset();
        pago.reiniciar();
    };

    // ─── API pública (idéntica al original) ───────────────────────────────
    return {
        // Navegación
        pasoActual,
        totalPasos: TOTAL_PASOS,

        // Contexto
        evento,

        // Paso 1 (Zona)
        zonas:             zonas.zonas,
        loadingZonas:      zonas.loadingZonas,
        errorZonas:        zonas.errorZonas,
        zonaSeleccionada:  zonas.zonaSeleccionada,
        svgRecinto:        zonas.svgRecinto,

        // Paso 2 (Butacas)
        asientosData:              butacas.asientosData,
        loadingAsientos:           butacas.loadingAsientos,
        errorAsientos:             butacas.errorAsientos,
        asientosSeleccionados:     butacas.asientosSeleccionados,
        maxAsientos,
        loadingBloquear:           butacas.loadingBloquear,
        errorBloquear:             butacas.errorBloquear,

        // Paso 3 (Datos)
        sessionId:           butacas.sessionId,
        formDatosComprador:  datos.formDatosComprador,
        formCamposCustom:    datos.formCamposCustom,
        formDatosPorButaca:  datos.formDatosPorButaca,
        teatroHash:          datos.teatroHash,
        loadingGuardarDatos: datos.loadingGuardarDatos,
        errorGuardarDatos:   datos.errorGuardarDatos,
        amountResult:        datos.amountResult,
        jetIdResult:         datos.jetIdResult,
        formularioResult:    datos.formularioResult,
        pagoRequerido:       datos.pagoRequerido,
        esPagoRedsys:        datos.esPagoRedsys,
        esPagoPaycomet:      datos.esPagoPaycomet,

        // Paso 4 (Resumen)
        terminosAceptados: resumen.terminosAceptados,
        loadingProcesar:   resumen.loadingProcesar,
        errorProcesar:     resumen.errorProcesar,
        resultadoCompra:   resumen.resultadoCompra,
        timerExpirado,

        // Paso 5 (Pago)
        paymentResultado:   pago.resultado,
        paymentLoading:     pago.loading,
        paymentIframeUrl:   pago.iframeUrl,
        paymentErrorMsg:    pago.errorMsg,
        paymentProcesarPago: pago.procesarPago,
        paymentReiniciar:    pago.reiniciar,

        // Computed guards
        puedeAvanzarPasoZona:     zonas.puedeAvanzarPasoZona,
        puedeAvanzarPasoButacas:  butacas.puedeAvanzarPasoButacas,
        puedeAvanzarPasoDatos:    datos.puedeAvanzarPasoDatos,
        puedeFinalizarPasoResumen,
        puedeIniciarFlujo,
        esFlujoSinPago:   datos.esFlujoSinPago,
        isZonasEmpty:     zonas.isZonasEmpty,
        isAsientosEmpty:  butacas.isAsientosEmpty,
        limitAsientosAlcanzado: butacas.limitAsientosAlcanzado,
        hasDatosPorButaca:      datos.hasDatosPorButaca,
        hasCamposCustom:        datos.hasCamposCustom,

        // Métodos: inicialización
        iniciar,
        setStatsRestantes,

        // Métodos: Paso 1
        fetchZonas:      zonas.fetchZonas,
        fetchSvgRecinto: zonas.fetchSvgRecinto,
        seleccionarZona,
        limpiarZona,

        // Métodos: Paso 2
        fetchAsientos:             butacas.fetchAsientos,
        toggleAsiento:             butacas.toggleAsiento,
        quitarAsiento:             butacas.quitarAsiento,
        limpiarAsientosSeleccionados: butacas.limpiarAsientosSeleccionados,

        // Métodos: Paso 3
        guardarDatosYAvanzar,
        liberarSesion: butacas.liberarSesion,

        // Métodos: Paso 4
        procesarCompra,

        // Métodos: navegación
        nextStep,
        prevStep,
        cancelar,

        // Métodos: timer
        onTimerExpirado,
    };
}
